import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect, useState } from "react";

import { DRAFT_STALE_TIME_MS } from "../constants";
import {
  applyCountAdjustments,
  overridesKeyOf,
  type CountAdjustment,
} from "../listCounts";
import { isSegmentCountable, type DraftBody } from "../mappers";

import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import type {
  SegmentVacancyListCounts,
  VacancyOverride,
} from "@/features/segments/types";
import { instance } from "@/lib/axios/instance";

type FetchedCounts = SegmentVacancyListCounts & { fetchedFor: string };

export function useSegmentListCounts(body: DraftBody) {
  const [adjustments, setAdjustments] = useState<CountAdjustment[]>([]);
  const query = useQuery<FetchedCounts, AxiosError>({
    queryKey: segmentsQueryKeys.listCounts(body),
    queryFn: ({ signal }) =>
      instance
        .post<SegmentVacancyListCounts>(
          "/api/v2/Segments/drafts/vacancies",
          { ...body, parts: "Counts" },
          { signal },
        )
        .then(({ data }) => ({
          includedCount: data.includedCount,
          excludedCount: data.excludedCount,
          fetchedFor: overridesKeyOf(body.vacancyOverrides),
        })),
    enabled: isSegmentCountable(body),
    staleTime: DRAFT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const fetchedFor = query.data?.fetchedFor;
  useEffect(() => {
    if (
      fetchedFor !== undefined &&
      adjustments[adjustments.length - 1]?.key === fetchedFor
    )
      setAdjustments([]);
  }, [fetchedFor, adjustments]);

  const adjust = (includedDelta: number, overridesAfter: VacancyOverride[]) => {
    if (includedDelta === 0) return;
    setAdjustments((previous) => [
      ...previous,
      { key: overridesKeyOf(overridesAfter), includedDelta },
    ]);
  };

  return {
    data: query.data && applyCountAdjustments(query.data, adjustments),
    isFetching: query.isFetching,
    adjust,
  };
}

export type SegmentListCounts = ReturnType<typeof useSegmentListCounts>;
